import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedChatMessage } from '../../chat/utils/chatMessageFormat';
import { MY_PATH_URL } from '../utils/pathsChatUrl';
import { displayText } from '../utils/displayText';
import GenerationPanel, { GenerationFailureBanner, GenerationThreadCard } from './GenerationPanel';
import PathView from './PathView';
import PlanningChatEmptyState from './PlanningChatEmptyState';
import { getPlanningEmptyStateLine } from './planningEmptyState';
import './PathsChatSurface.css';

// What ChatAgentPanel mounts for Paths. `controller` is usePathsChat's
// return value; `children` is ChatAgentPanel's own chat column (its message
// list and composer), so the composer the student already knows is the one
// that talks to the planner.
//
//   plan    PathView fills the main area; the chat column is not shown
//   split   the chat column, with PathView as a panel beside it
//   thread  the chat column only (a planning conversation)
//   none    the chat column untouched
//
// At 860px and below an open panel replaces the chat column, with a toggle
// back to the chat (decision 16). ChatAgentPanel's own 768px sidebar drawer
// is unchanged.

export const NARROW_QUERY = '(max-width: 860px)';

function matchNarrow() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(NARROW_QUERY)
    : null;
}

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => Boolean(matchNarrow()?.matches));
  useEffect(() => {
    const query = matchNarrow();
    if (!query) return undefined;
    const onChange = (e) => setNarrow(Boolean(e.matches));
    setNarrow(Boolean(query.matches));
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener?.(onChange);
    return () => query.removeListener?.(onChange);
  }, []);
  return narrow;
}

function BotAvatar() {
  return (
    <div className="chat-agent-bot-avatar-badge" title="uAI Assistant">
      <img src="/logo-yellow.png" alt="" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
    </div>
  );
}

// The planning or revision conversation, drawn with the chat's own message
// classes so it reads as the same chat. The draft plan is a sibling below
// the messages, never chat text (decision 4).
export function PathsThread({ controller }) {
  const {
    session, sessionState, messages, proposal, generating, generationNotice, error,
    planSessionId, sending, showPlanningEmpty, panelOpen,
  } = controller;
  const endRef = useRef(null);
  // Identity of the visible thread: a new row, a longer streamed reply, the
  // thinking indicator, or a status line under the messages. Same content
  // does not scroll again.
  const threadTail = [
    messages.map((m) => `${m.id}:${displayText(m.message).length}`).join('|'),
    sending ? 'sending' : '',
    generating ? `generating:${controller.generationPhase || ''}` : '',
    generationNotice || '',
    error || '',
    proposal?.version ? `proposal:${proposal.version}` : '',
    panelOpen ? 'panel' : '',
  ].join('\n');

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'auto', block: 'end' });
  }, [threadTail]);

  if (sessionState === 'unavailable') {
    return (
      <div className="chat-agent-messages chat-agent-messages-fullpage">
        <div className="chat-agent-messages-inner paths-thread-unavailable" role="alert">
          <h2>This conversation isn&apos;t available</h2>
          <p>It may have been closed, or it can&apos;t be loaded right now.</p>
          <Link className="path-view-btn path-view-btn-primary" to={MY_PATH_URL}>Back to My Path</Link>
        </div>
      </div>
    );
  }

  // showPlanningEmpty already excludes a resume that has not loaded, so a
  // conversation never flashes the greeting before its messages arrive.
  const emptyLine = getPlanningEmptyStateLine(planSessionId || null);

  return (
    <div className="chat-agent-messages chat-agent-messages-fullpage">
      <div className="chat-agent-messages-inner">
        {showPlanningEmpty && <PlanningChatEmptyState line={emptyLine} />}
        {messages.map((m) => {
          const isUser = m.sender === 'user';
          const text = displayText(m.message);
          if (!text) return null;
          return (
            <div key={m.id} className={`chat-agent-msg-row ${isUser ? 'user' : 'bot'}`}>
              {!isUser && <BotAvatar />}
              <div className={`chat-agent-msg ${isUser ? 'user' : 'bot'}`}>
                {isUser
                  ? <div className="chat-agent-msg-content">{text}</div>
                  : <FormattedChatMessage text={text} partial={String(m.id).startsWith('pending-')} />}
              </div>
            </div>
          );
        })}
        {sending && !messages.some((m) => String(m.id).startsWith('pending-bot')) && (
          <div className="chat-agent-msg-row bot thinking">
            <BotAvatar />
            <div className="chat-agent-msg bot" aria-label="The planner is thinking">
              <div className="chat-agent-typing-dots">
                <span className="chat-agent-typing-dot" />
                <span className="chat-agent-typing-dot" />
                <span className="chat-agent-typing-dot" />
              </div>
            </div>
          </div>
        )}
        {(generating || generationNotice || proposal?.version) && (
          <GenerationThreadCard
            title={controller.generationTitle}
            startedAt={controller.generationStartedAt}
            serverNow={controller.serverNow}
            generating={generating}
            failed={Boolean(generationNotice)}
            panelOpen={panelOpen}
            onOpen={controller.openPanel}
            onRetry={controller.updating ? controller.updateDraft : controller.generate}
            updating={controller.updating}
            changeCount={(controller.pendingChanges || []).length}
            counts={controller.nothingApplied ? null : (controller.proposal?.changes?.diff?.counts || null)}
            nothingApplied={controller.nothingApplied}
            unappliedCount={(controller.proposal?.changes?.unapplied || []).length}
          />
        )}
        {error && <p className="paths-thread-error" role="alert">{error}</p>}
        <div ref={endRef} data-paths-thread-end="1" />
      </div>
    </div>
  );
}

// Above the composer while a planning session is ready: "Draft my plan".
// Also after a generation that did not finish (or is past finishing), so
// the student can try again even after a reload.
export function PathsComposerAccessory({ controller }) {
  // Once a draft has been asked for, the thread card and the panel own
  // progress, the result, and Try again. The pill is only the first click.
  // A waiting change list takes that same slot, and stays hidden while the
  // planner is still replying.
  if (!controller.threadActive || controller.sending) return null;
  const pending = controller.pendingChanges || [];
  const showUpdate = controller.readyToUpdate && pending.length > 0 && !controller.generating;
  if (showUpdate) {
    return (
      <div className="paths-composer-accessory">
        <p className="paths-changes-label">Changes to make ({pending.length})</p>
        <ul className="paths-changes-list">
          {pending.map((change) => <li key={change}>{change}</li>)}
        </ul>
        <button
          type="button"
          className="paths-draft-btn"
          onClick={controller.updateDraft}
          disabled={controller.sending}
        >
          Update draft
        </button>
      </div>
    );
  }
  if (controller.proposal) return null;
  if (!controller.readyToGenerate || controller.generating || controller.generationNotice) return null;
  return (
    <div className="paths-composer-accessory">
      <button
        type="button"
        className="paths-draft-btn"
        onClick={controller.generate}
        disabled={controller.sending}
      >
        Draft my plan
      </button>
    </div>
  );
}

function generationPanelProps(controller, narrow) {
  return {
    title: controller.generationTitle,
    phase: controller.generationPhase,
    startedAt: controller.generationStartedAt,
    serverNow: controller.serverNow,
    generating: controller.generating,
    failed: Boolean(controller.generationNotice),
    proposal: controller.proposal,
    draftPath: controller.draftPath,
    mode: controller.session?.purpose === 'revision' ? 'revision' : 'draft',
    accepting: controller.accepting,
    onAccept: controller.accept,
    onRequestChange: () => {
      controller.requestChanges();
      if (narrow) controller.closePanel();
    },
    onDiscard: controller.discard,
    onRetry: controller.updating ? controller.updateDraft : controller.generate,
    onClose: controller.closePanel,
    updating: controller.updating,
    pendingChanges: controller.pendingChanges,
    baseline: controller.baseline,
    awaitingUpdate: Boolean(controller.readyToUpdate) && !controller.generating,
    nothingApplied: Boolean(controller.nothingApplied),
  };
}

function SplitSurface({ controller, children, panel }) {
  const narrow = useIsNarrow();
  const showPanel = controller.panelOpen;
  const showChat = !(narrow && showPanel);
  return (
    <div className={`paths-surface paths-surface-split${showPanel ? ' paths-surface-panel-open' : ''}`}>
      {showChat && (
        <div className="paths-chat-column">
          {!showPanel && (
            <div className="paths-panel-toggle-row">
              <button type="button" className="paths-panel-toggle" onClick={controller.openPanel}>Show plan</button>
            </div>
          )}
          {children}
        </div>
      )}
      {showPanel && (
        <aside className="paths-panel" aria-label="Plan">
          {narrow && (
            <div className="paths-panel-toggle-row">
              <button type="button" className="paths-panel-toggle" onClick={controller.closePanel}>Back to chat</button>
            </div>
          )}
          {panel}
        </aside>
      )}
    </div>
  );
}

export default function PathsChatSurface({ controller, children }) {
  const { layout, pathId, panelOpen } = controller;
  const narrow = useIsNarrow();
  const generationRelevant = Boolean(
    controller.generating || controller.proposal?.version || controller.generationNotice
  );

  if (layout === 'none') return children;
  if (layout === 'thread' && !(generationRelevant && panelOpen)) {
    return <div className="paths-surface paths-surface-thread">{children}</div>;
  }

  if (layout === 'plan') {
    return (
      <div className="paths-surface paths-surface-plan">
        <PathView
          key={pathId}
          pathId={pathId}
          layout="full"
          reloadKey={controller.pathReloadKey}
          onDiscuss={() => controller.discuss()}
          onRevise={(text) => controller.discuss(text)}
          onPathChanged={controller.notifyPathsChanged}
        />
      </div>
    );
  }

  // A failed revision must not replace the accepted plan. The failure is a
  // banner above it (or above an unaccepted draft). A pathless creation
  // has nothing to put back, so the failure stays the panel.
  const failed = Boolean(controller.generationNotice) && !controller.generating;
  const showBanner = failed && Boolean(pathId) && !controller.updating;
  const showGenerationPanel = Boolean(
    controller.generating || controller.proposal?.version || (failed && !pathId)
  );
  const body = showGenerationPanel
    ? <GenerationPanel {...generationPanelProps(controller, narrow)} failed={showBanner ? false : failed} />
    : (
      <PathView
        key={pathId}
        pathId={pathId}
        layout="panel"
        reloadKey={controller.pathReloadKey}
        onClose={controller.closePanel}
        onRevise={(text) => controller.discuss(text)}
        onPathChanged={controller.notifyPathsChanged}
      />
    );
  const panel = (
    <>
      {showBanner && <GenerationFailureBanner onRetry={controller.generate} />}
      {failed && controller.updating && !showGenerationPanel && (
        <div className="paths-gen-banner" role="alert">
          <p>Couldn't update the draft. Your previous draft is unchanged.</p>
          <button type="button" className="paths-draft-btn" onClick={controller.updateDraft}>Try again</button>
        </div>
      )}
      {body}
    </>
  );
  return <SplitSurface controller={controller} panel={panel}>{children}</SplitSurface>;
}
